import React, { useCallback, useEffect, useState } from 'react';
import '../../../components/layout/ModuleLayout.css';
import AuditInsights from '../../../components/audits/AuditInsights';
import AuditAgencies from '../../../components/audits/AuditAgencies';
import AuditUniverse from '../../../components/audits/AuditUniverse';
import WorkflowModal from '../../../components/common/WorkflowModal';
import NoDataFound from '../../../components/common/NoDataFound';
import AgencyModal from '../../../components/audits/AgencyModal';
import AuditPrograms from '../../../components/audits/AuditPrograms';
import ProgramModal from '../../../components/audits/ProgramModal';
import ProgramConfigModal from '../../../components/audits/ProgramConfigModal';
import AgencyMembersModal from '../../../components/audits/AgencyMembersModal';
import AgencyConfigureModal from '../../../components/audits/AgencyConfigureModal';
import AuditProcedures from '../../../components/audits/AuditProcedures';
import ProcedureModal from '../../../components/audits/ProcedureModal';
import AuditSchedule from '../../../components/audits/AuditSchedule';
import AuditFindings from '../../../components/audits/AuditFindings';
import ProcedureConfigModal from '../../../components/audits/ProcedureConfigModal';
import QueriesModule from '../../audit/QueriesModule';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import CadenceRuleList from '../../../components/cadence/CadenceRuleList';
import ReportTemplateNetBody from '../../grc/ReportTemplateNetBody';
import { CADENCE_MODULE_TYPES } from '../../../components/cadence/cadenceModuleTypes';
import { confirmWarning, confirmActive } from '../../../components/common/confirm';
import {
    Agency, AgencyCreatePayload,
    AgencyMember,
    ProcedureTemplate, ProcedureTemplateCreatePayload,
    ProgramTemplate,
    ProgramProcedureMap,
    agencyService, agencyMemberService,
    procedureTemplateService,
    programTemplateService, programProcedureMapService,
    auditPersonaService, type MyAuditPersona,
} from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';
import { AuditTabKey } from './AuditTabs';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermission } from '../../../contexts/PermissionContext';
import type { LaunchUser } from '../../../services/auditService';

interface AuditNetBodyProps {
  activeTab?: AuditTabKey;
}

const AuditNetBody: React.FC<AuditNetBodyProps> = ({ activeTab = 'Insights' }) => {
    // Plan 04b-04 / DRC-07 — derive a LaunchUser shape from the auth context so
    // AuditPrograms can gate the Launch button via isLaunchable().
    const { state: authState } = useAuth();
    // RBAC gate via activity grant (backend independently enforces
    // _AGENCY_CREATOR_ROLES via require_permission on ADT_AGENCIES).
    // Hide Create button for unauthorized users so they don't hit a dead-end 403.
    const { allowed: canCreateAgencyRole } = usePermission('ADT_AGENCIES', 'view+modify');

    // Entry-point permission gating (2026-06-20 revision): gate the toolbar
    // +Create button and row edit/action icons, not just the modal submit, so
    // an unauthorized user doesn't open a modal and hit a dead-end greyed Save.
    // Activity-based gate per the canonical GRC-Team model.
    const { allowed: canViewAgencies, loading: viewLoadingAgencies } = usePermission('ADT_AGENCIES', 'view');
    const { allowed: canModifyAgencies, loading: permLoadingAgencies } = usePermission('ADT_AGENCIES', 'view+modify');
    const { allowed: canViewPrograms, loading: viewLoadingPrograms } = usePermission('ADT_PROGRAMS', 'view');
    const { allowed: canModifyPrograms, loading: permLoadingPrograms } = usePermission('ADT_PROGRAMS', 'view+modify');
    const { allowed: canViewProcedures, loading: viewLoadingProcedures } = usePermission('ADT_PROCEDURES', 'view');
    const { allowed: canModifyProcedures, loading: permLoadingProcedures } = usePermission('ADT_PROCEDURES', 'view+modify');
    const { allowed: canViewReports, loading: viewLoadingReports } = usePermission('ADT_REPORTS', 'view');
    const { allowed: canViewSchedule, loading: viewLoadingSchedule } = usePermission('ADT_SCHEDULE', 'view');
    const { allowed: canViewFindings, loading: viewLoadingFindings } = usePermission('ADT_FINDINGS', 'view');
    const { allowed: canViewComms, loading: viewLoadingComms } = usePermission('ADT_COMMUNICATIONS', 'view');

    // Agencies: keep the existing role-based domain rule AND the activity grant.
    // Both must hold (role IS the intended creator-scope rule; activity is the
    // RBAC extent). AND-ing them is strictly more restrictive and safe.
    const canCreateAgency = canCreateAgencyRole && canModifyAgencies;
    const launchUser: LaunchUser | null = authState.user
      ? {
          id: authState.user.id,
          roles: authState.user.roles ?? [],
          // agency_id is not currently exposed on User; backend re-checks Agency
          // Head scope on every launch, so undefined is safe here.
          agency_id: (authState.user as { agency_id?: number }).agency_id ?? undefined,
        }
      : null;

    // Audit-authoring persona (external auditor vs GRC). Drives Create gating +
    // forces Agency-Private scope on the create modals for an external audit lead.
    const [persona, setPersona] = useState<MyAuditPersona | null>(null);
    useEffect(() => {
        let cancelled = false;
        auditPersonaService.me()
            .then(p => { if (!cancelled) setPersona(p); })
            .catch(() => { /* non-fatal — default to GRC persona (null) */ });
        return () => { cancelled = true; };
    }, []);
    // GRC persona = pure RBAC, GRC-Common only. Agency-Private authoring belongs
    // exclusively to the /lead portal (LeadProgramsBody/LeadProceduresBody), so the
    // GRC module NEVER puts the modals into lead mode — even if this GRC user also
    // heads an agency. Always empty here; the agency role takes effect only in the
    // Audit persona. (Was persona.authored_agencies — that leaked the Agency-Private
    // scope option into the GRC Create modal.)
    const leadAgencies: { id: number; name: string }[] = [];
    // An external member who is NOT the agency head may view but not author —
    // hide the Create entry points for them (the backend would 403 anyway).
    const externalViewerOnly = !!persona?.is_external && !persona.can_author;
    const canCreatePrograms = canModifyPrograms && !externalViewerOnly;
    const canCreateProcedures = canModifyProcedures && !externalViewerOnly;

    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [agencyMembers, setAgencyMembers] = useState<AgencyMember[]>([]);
    const [procedures, setProcedures] = useState<ProcedureTemplate[]>([]);
    const [procRefreshNonce, setProcRefreshNonce] = useState(0);
    const [programs, setPrograms] = useState<ProgramTemplate[]>([]);
    const [programMaps, setProgramMaps] = useState<ProgramProcedureMap[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const showSuccess = (message: string) => setToast({ type: 'success', message });
    const showError   = (message: string) => setToast({ type: 'error',   message });

    const reload = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            // Agencies + members may 403 for users without the ADT_AGENCIES grant;
            // swallow to [] so a denial on that tab does not blank Programs /
            // Procedures (the Agencies tab itself shows the "Not authorised" gate).
            // Per-tab lists may 403 for users lacking that activity's grant; swallow
            // to [] so a denial on one tab does not blank the others (each gated tab
            // shows its own "Not authorised" screen).
            const [a, m, p, pr, maps] = await Promise.all([
                agencyService.list().catch(() => []),
                agencyMemberService.list().catch(() => []),
                procedureTemplateService.list().catch(() => []),
                programTemplateService.list().catch(() => []),
                programProcedureMapService.list().catch(() => []),
            ]);
            setAgencies(a.filter(x => (x.is_active ?? 1) !== -1));
            setAgencyMembers(m.filter(x => (x.is_active ?? 1) !== -1));
            setProcedures(p.filter(x => (x.is_active ?? 1) !== -1));
            setPrograms(pr.filter(x => (x.is_active ?? 1) !== -1));
            setProgramMaps(maps.filter(x => (x.is_active ?? 1) !== -1));
        } catch (e: any) {
            setLoadError(extractApiError(e, 'Failed to load audit data.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const [isAgencyModalOpen, setAgencyModalOpen] = useState(false);
    const [currentAgency, setCurrentAgency] = useState<Agency | null>(null);

    const [isProcModalOpen, setProcModalOpen] = useState(false);
    const [currentProc, setCurrentProc] = useState<ProcedureTemplate | null>(null);
    const [isConfigModalOpen, setConfigModalOpen] = useState(false);
    const [currentConfigProc, setCurrentConfigProc] = useState<ProcedureTemplate | null>(null);

    const [isProgramModalOpen, setProgramModalOpen] = useState(false);
    const [currentProgram, setCurrentProgram] = useState<ProgramTemplate | null>(null);

    const [isProgramConfigOpen, setProgramConfigOpen] = useState(false);
    const [configProgram, setConfigProgram] = useState<ProgramTemplate | null>(null);

    const [isMembersModalOpen, setMembersModalOpen] = useState(false);
    const [membersAgency, setMembersAgency] = useState<Agency | null>(null);

    const [isConfigureModalOpen, setConfigureModalOpen] = useState(false);
    const [configureAgency, setConfigureAgency] = useState<Agency | null>(null);

    // Status-dot workflow modal (submit/approve/reject/reopen via centralized engine).
    const [wf, setWf] = useState<{ kind: string; id: number } | null>(null);

    // Plan 04-04: AuditSchedule now self-fetches via scheduleService —
    // no scheduleData/isSchedulerRunning/INITIAL_SCHEDULE state needed.
    // Plan 04-07: Findings mock state + ResolutionModal removed —
    // AuditFindings self-fetches via findingsService and owns its own modal.

    // ── Agency handlers ──────────────────────────────────────────────────────
    const handleSaveAgency = async (data: AgencyCreatePayload, id?: number) => {
        try {
            if (id) await agencyService.update(id, data);
            else    await agencyService.create(data);
            setAgencyModalOpen(false);
            setCurrentAgency(null);
            await reload();
            showSuccess(id ? 'Agency updated successfully.' : 'Agency created successfully.');
        } catch (e: any) {
            showError(extractApiError(e, 'Save failed.'));
        }
    };

    const handleInactivateAgency = async (id: number) => {
        const ok = await confirmWarning({ title: 'Inactivate Agency?', text: 'The record will be retained but marked Inactive.', confirmText: 'Yes, Inactivate' });
        if (!ok) return;
        try {
            await agencyService.inactivate(id);
            await reload();
            setToast({ type: 'warning', message: 'Agency inactivated.' });
        } catch (e: any) {
            showError(extractApiError(e, 'Inactivation failed.'));
        }
    };

    const handleReactivateAgency = async (id: number) => {
        const ok = await confirmActive({ title: 'Reactivate Agency?', confirmText: 'Yes, Reactivate' });
        if (!ok) return;
        try {
            await agencyService.reactivate(id);
            await reload();
            showSuccess('Agency reactivated.');
        } catch (e: any) {
            showError(extractApiError(e, 'Reactivation failed.'));
        }
    };

    // ── Procedure handlers ───────────────────────────────────────────────────
    const handleSaveProcedure = async (data: ProcedureTemplateCreatePayload, id?: number) => {
        try {
            const saved = id
                ? await procedureTemplateService.update(id, data)
                : await procedureTemplateService.create(data);
            setProcModalOpen(false);
            setCurrentProc(null);
            if (currentConfigProc?.id === saved.id) setCurrentConfigProc(saved);
            await reload();
            setProcRefreshNonce(n => n + 1);
            showSuccess(id ? 'Procedure updated successfully.' : 'Procedure created successfully.');
        } catch (e: any) {
            showError(extractApiError(e, 'Save failed.'));
        }
    };

    const handleSaveProcedureDetails = async (data: ProcedureTemplateCreatePayload, id?: number) => {
        try {
            const saved = await procedureTemplateService.update(id!, data);
            setCurrentConfigProc(saved);
            await reload();
            setProcRefreshNonce(n => n + 1);
            showSuccess('Procedure updated successfully.');
        } catch (e: any) {
            showError(extractApiError(e, 'Save failed.'));
        }
    };

    const handleToggleProcedureActive = async (id: number, next: 0 | 1) => {
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? 'Reactivate this procedure?' : 'Inactivate this procedure?',
            text: next
                ? 'It will appear in active procedure lists again.'
                : 'It will be hidden from active procedure lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        try {
            const updated = next
                ? await procedureTemplateService.reactivate(id)
                : await procedureTemplateService.inactivate(id);
            if (currentProc?.id === id) setCurrentProc(updated);
            if (currentConfigProc?.id === id) setCurrentConfigProc(updated);
            await reload();
            setProcRefreshNonce(n => n + 1);
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Procedure reactivated.' : 'Procedure inactivated.' });
        } catch (e: any) {
            showError(extractApiError(e, 'Status update failed.'));
        }
    };

    // ── Program handlers ─────────────────────────────────────────────────────
    const handleSaveProgramDetails = async (
        data: import('../../../services/auditService').ProgramTemplateCreatePayload,
        id?: number,
    ) => {
        try {
            const saved = id
                ? await programTemplateService.update(id, data)
                : await programTemplateService.create(data);
            setProgramModalOpen(false);
            setCurrentProgram(null);
            if (configProgram?.id === saved.id) setConfigProgram(saved);
            await reload();
            showSuccess(id ? 'Program updated successfully.' : 'Program created successfully.');
        } catch (e: any) {
            showError(extractApiError(e, 'Save failed.'));
        }
    };

    const handleInactivateProgram = async (id: number) => {
        const ok = await confirmWarning({ title: 'Inactivate Program Template?', text: 'The record will be retained but marked Inactive. In-flight instances continue unaffected.', confirmText: 'Yes, Inactivate' });
        if (!ok) return;
        try {
            await programTemplateService.inactivate(id);
            setProgramConfigOpen(false);
            setConfigProgram(null);
            await reload();
            setToast({ type: 'warning', message: 'Program template inactivated.' });
        } catch (e: any) {
            showError(extractApiError(e, 'Inactivation failed.'));
        }
    };

    const handleReactivateProgram = async (id: number) => {
        const ok = await confirmActive({ title: 'Reactivate Program Template?', confirmText: 'Yes, Reactivate' });
        if (!ok) return;
        try {
            await programTemplateService.reactivate(id);
            setProgramConfigOpen(false);
            setConfigProgram(null);
            await reload();
            showSuccess('Program template reactivated.');
        } catch (e: any) {
            showError(extractApiError(e, 'Reactivation failed.'));
        }
    };

    // ── Program-procedure map handlers ───────────────────────────────────────
    const handleAddProcedure = async (programId: number, procedureId: number) => {
        try {
            await programProcedureMapService.create({
                program_template_id: programId,
                procedure_template_id: procedureId,
            });
            await reload();
            setConfigProgram(prev => prev ? programs.find(p => p.id === prev.id) ?? prev : prev);
            showSuccess('Procedure added to program.');
        } catch (e: any) {
            showError(extractApiError(e, 'Failed to add procedure.'));
        }
    };

    const handleRemoveProcedure = async (mapId: number) => {
        try {
            await programProcedureMapService.remove(mapId);
            await reload();
            showSuccess('Procedure removed from program.');
        } catch (e: any) {
            showError(extractApiError(e, 'Failed to remove procedure.'));
        }
    };

    const handleReorderProcedure = async (mapId: number, newSeq: number) => {
        try {
            await programProcedureMapService.updateSequence(mapId, newSeq);
            await reload();
        } catch (e: any) {
            showError(extractApiError(e, 'Failed to reorder procedure.'));
        }
    };

    // ── Derived counts ───────────────────────────────────────────────────────
    const memberCounts = agencyMembers.reduce<Record<number, number>>((acc, m) => {
        acc[m.agency_id] = (acc[m.agency_id] ?? 0) + 1;
        return acc;
    }, {});
    const procedureCountsByProgram = programMaps.reduce<Record<number, number>>((acc, m) => {
        acc[m.program_template_id] = (acc[m.program_template_id] ?? 0) + 1;
        return acc;
    }, {});
    const mapsForConfigProgram = configProgram
        ? programMaps.filter(m => m.program_template_id === configProgram.id)
        : [];

    // ── Schedule / Findings ──────────────────────────────────────────────────
    // Both tabs now own their data fetch + interaction state:
    //   - Schedule: plan 04-04 wired AuditSchedule to scheduleService.
    //   - Findings: plan 04-05 wired AuditFindings to findingsService and
    //     embeds its own FindingDetailModal placeholder (phase 06 swaps body).
    // Mock runner (runScheduler) and inline resolution handler removed — see
    // plan 04-07.

    // ── Panel renderer ───────────────────────────────────────────────────────
    const renderPanel = () => {
        if (activeTab === 'Insights') return <AuditInsights />;
        if (activeTab === 'Universe') return <AuditUniverse />;
        if (loadError) {
            return <div className="p-6 text-red-600 text-sm border border-red-200 bg-red-50 rounded">{loadError}</div>;
        }
        if (loading && (activeTab === 'Agencies' || activeTab === 'Procedures')) {
            return <div className="p-12 text-center text-gray-400">Loading…</div>;
        }
        if (activeTab === 'Agencies') {
            if (!viewLoadingAgencies && !canViewAgencies) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_AGENCIES'." />
                    </div>
                );
            }
            return (
                <AuditAgencies
                    agencies={agencies}
                    memberCounts={memberCounts}
                    onAdd={() => { setCurrentAgency(null); setAgencyModalOpen(true); }}
                    onEdit={(agency) => { setCurrentAgency(agency); setAgencyModalOpen(true); }}
                    onInactivate={handleInactivateAgency}
                    onReactivate={handleReactivateAgency}
                    onManageMembers={(agency) => { setMembersAgency(agency); setMembersModalOpen(true); }}
                    onConfigure={(agency) => { setConfigureAgency(agency); setConfigureModalOpen(true); }}
                    onWorkflow={(id) => setWf({ kind: 'adt_agency', id })}
                    canCreate={canCreateAgency}
                    permLoading={permLoadingAgencies}
                />
            );
        }
        if (activeTab === 'Programs') {
            if (!viewLoadingPrograms && !canViewPrograms) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_PROGRAMS'." />
                    </div>
                );
            }
            return (
                <AuditPrograms
                    programs={programs}
                    agencies={agencies}
                    procedureCounts={procedureCountsByProgram}
                    onAdd={() => { setCurrentProgram(null); setProgramModalOpen(true); }}
                    onEdit={(prog) => { setCurrentProgram(prog); setProgramModalOpen(true); }}
                    onConfigure={(prog) => { setConfigProgram(prog); setProgramConfigOpen(true); }}
                    onWorkflow={(id) => setWf({ kind: 'adt_program_template', id })}
                    currentUser={launchUser}
                    onProgramLaunched={() => { void reload(); showSuccess('Program launched.'); }}
                    canModify={canCreatePrograms}
                    permLoading={permLoadingPrograms}
                />
            );
        }
        if (activeTab === 'Procedures') {
            if (!viewLoadingProcedures && !canViewProcedures) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_PROCEDURES'." />
                    </div>
                );
            }
            return (
                <AuditProcedures
                    agencies={agencies}
                    refreshNonce={procRefreshNonce}
                    onAdd={() => { setCurrentProc(null); setProcModalOpen(true); }}
                    onConfigure={(proc) => { setCurrentConfigProc(proc); setConfigModalOpen(true); }}
                    onWorkflow={(id) => setWf({ kind: 'adt_procedure', id })}
                    canModify={canCreateProcedures}
                    permLoading={permLoadingProcedures}
                />
            );
        }
        if (activeTab === 'Reports') {
            if (!viewLoadingReports && !canViewReports) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_REPORTS'." />
                    </div>
                );
            }
            return <ReportTemplateNetBody />;
        }
        if (activeTab === 'Schedule') {
            if (!viewLoadingSchedule && !canViewSchedule) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_SCHEDULE'." />
                    </div>
                );
            }
            return <AuditSchedule />;
        }
        if (activeTab === 'Findings') {
            if (!viewLoadingFindings && !canViewFindings) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_FINDINGS'." />
                    </div>
                );
            }
            return <AuditFindings />;
        }
        if (activeTab === 'Communications') {
            if (!viewLoadingComms && !canViewComms) {
                return (
                    <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_COMMUNICATIONS'." />
                    </div>
                );
            }
            // External communications raised by audit agencies to the GRC team.
            // The GRC user is not an agency member, so the backend visibility rule
            // surfaces external threads here; internal (agency-only) threads stay hidden.
            return <QueriesModule channelLock="external" />;
        }
        if (activeTab === 'Cadence') {
            return <CadenceRuleList allowedTypeCodes={CADENCE_MODULE_TYPES.audit} />;
        }
        return (
            <div className="coming-soon">
                <span className="soon-badge">Coming soon</span>
                <p>{activeTab} features will appear here shortly.</p>
            </div>
        );
    };

    return (
        <>
            <div className="module-layout">
                <div className="module-main">
                    <div className="tab-content">
                        <section role="tabpanel" className="tab-panel show">
                            {renderPanel()}
                        </section>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <AgencyModal
                isOpen={isAgencyModalOpen}
                onClose={() => setAgencyModalOpen(false)}
                onSave={handleSaveAgency}
                initialData={currentAgency}
                onInactivate={async (id) => { await handleInactivateAgency(id); setAgencyModalOpen(false); }}
                onReactivate={async (id) => { await handleReactivateAgency(id); setAgencyModalOpen(false); }}
            />
            <ProgramModal
                isOpen={isProgramModalOpen}
                onClose={() => { setProgramModalOpen(false); setCurrentProgram(null); }}
                onSave={handleSaveProgramDetails}
                initialData={currentProgram}
                agenciesList={agencies}
                leadAgencies={leadAgencies}
                onInactivate={async (id) => { await handleInactivateProgram(id); setProgramModalOpen(false); setCurrentProgram(null); }}
                onReactivate={async (id) => { await handleReactivateProgram(id); setProgramModalOpen(false); setCurrentProgram(null); }}
            />
            <ProgramConfigModal
                isOpen={isProgramConfigOpen}
                onClose={() => { setProgramConfigOpen(false); setConfigProgram(null); }}
                program={configProgram}
                agencies={agencies}
                procedures={procedures}
                programMaps={mapsForConfigProgram}
                onSaveDetails={handleSaveProgramDetails}
                onAddProcedure={handleAddProcedure}
                onRemoveProcedure={handleRemoveProcedure}
                onReorderProcedure={handleReorderProcedure}
                onInactivate={handleInactivateProgram}
                onReactivate={handleReactivateProgram}
            />
            <AgencyMembersModal
                isOpen={isMembersModalOpen}
                onClose={() => { setMembersModalOpen(false); setMembersAgency(null); }}
                agency={membersAgency}
                onChanged={reload}
            />
            <AgencyConfigureModal
                isOpen={isConfigureModalOpen}
                onClose={() => { setConfigureModalOpen(false); setConfigureAgency(null); }}
                agency={configureAgency}
                allPrograms={programs}
                onChanged={reload}
                initialTab="users"
                onEdit={(agency) => { setCurrentAgency(agency); setAgencyModalOpen(true); }}
            />
            <ProcedureModal
                isOpen={isProcModalOpen}
                onClose={() => setProcModalOpen(false)}
                onSave={handleSaveProcedure}
                initialData={currentProc}
                agencies={agencies}
                leadAgencies={leadAgencies}
                onToggleActive={handleToggleProcedureActive}
            />
            <ProcedureConfigModal
                isOpen={isConfigModalOpen}
                onClose={() => { setConfigModalOpen(false); setCurrentConfigProc(null); }}
                procedure={currentConfigProc}
                agencies={agencies}
                onSave={handleSaveProcedureDetails}
                onToggleActive={handleToggleProcedureActive}
            />

            {/* Status-dot workflow modal — submit/approve/reject/reopen */}
            <WorkflowModal
                isOpen={!!wf}
                onClose={() => setWf(null)}
                kind={wf?.kind ?? null}
                id={wf?.id ?? null}
                onChanged={() => { void reload(); setProcRefreshNonce(n => n + 1); }}
            />

            {/* Toast notifications */}
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default AuditNetBody;
